import { useEffect, useMemo, type ReactNode } from 'react'
import { useTheme } from '@/contexts/ThemeContext'
import type { SyncChange, SyncRun } from '@/api/types/readiness'
import { findObjective, domainColor } from '@/lib/indexes/registry'
import type { ModuleCtx } from '@/lib/readiness/module'
import { useReadinessStore } from '@/stores/readinessStore'
import { RdModal } from '@/components/readiness/RdModal'
import { DomainDot, Icon, Chip } from '@/components/readiness/primitives'
import { CoverageChip } from '@/components/readiness/ProgressParts'
import { OriginLink } from '@/lib/readiness/nav'
import { formatDate } from '@/components/common/format'
import { levelCode, type ActivityEntry } from '@/api/clients/assessmentsApi'

// ── Sync change summary (Iteration 5 §16) ───────────────────────────────────
// Read-only modal over one `readiness_sync_run`: its changes grouped (levels,
// evidence added, coverage, dependencies, migration items), each row linking
// to where the change lives with origin = the current page. Same styling and
// row pattern as the Objectives moved quick view (§8).

const TRIGGER_LABEL: Record<SyncRun['trigger'], string> = { manual: 'manual', scheduled: 'daily', on_open: 'on open' }
const GROUPS: Array<{ key: SyncChange['group']; label: string }> = [
  { key: 'levels', label: 'Levels' },
  { key: 'evidence', label: 'Evidence added' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'dependencies', label: 'Dependencies' },
  { key: 'items', label: 'Migration items' },
]

export function SyncSummaryModal({ ctx, runId, onClose }: { ctx: ModuleCtx; runId: string; onClose: () => void }) {
  const { theme } = useTheme()
  const { index, scope, orgId, indexId, orgName } = ctx
  const run = scope.sync_runs.find((r) => r.id === runId) ?? null
  const track = useReadinessStore((s) => s.track)
  const markViewed = useReadinessStore((s) => s.markSyncViewed)
  useEffect(() => {
    if (!run) return
    markViewed(run.id)
    track(orgId, indexId, { name: 'readiness.sync_summary_opened', trigger: run.trigger })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id])
  const changes = useMemo(() => run?.changes ?? [], [run])
  const q = `?index=${indexId}`
  const grouped = useMemo(() => GROUPS.map((g) => ({ ...g, rows: changes.filter((c) => c.group === g.key) })).filter((g) => g.rows.length), [changes])
  if (!run) return null

  const objectiveRow = (code: string, body: ReactNode, key: string) => {
    const f = findObjective(index, code)
    return (
      <li key={key} style={{ borderTop: '1px solid var(--rd-border-subtle)' }}>
        <OriginLink to={`/readiness/${code}${q}`} className="rd-qv-row flex items-center gap-3 px-6 py-2.5 text-[13px]" style={{ color: 'inherit', textDecoration: 'none' }} data-testid={`sync-row-${code}`}>
          {f ? <DomainDot domain={f.domain} theme={theme} size={10} /> : <span className="inline-block h-2.5 w-2.5" />}
          <span className="rd-mono rd-id font-semibold">{code}</span>
          <span className="min-w-0 flex-1 truncate" title={f?.objective.title}>{body}</span>
          <Icon name="chevron-right" size={16} className="rd-qv-chevron shrink-0" style={{ color: 'var(--rd-text-faint)' }} />
        </OriginLink>
      </li>
    )
  }
  const plainRow = (to: string | null, body: ReactNode, key: string, lead?: ReactNode) => (
    <li key={key} style={{ borderTop: '1px solid var(--rd-border-subtle)' }}>
      {to ? (
        <OriginLink to={to} className="rd-qv-row flex items-center gap-3 px-6 py-2.5 text-[13px]" style={{ color: 'inherit', textDecoration: 'none' }}>
          {lead}<span className="min-w-0 flex-1 truncate">{body}</span><Icon name="chevron-right" size={16} className="rd-qv-chevron shrink-0" style={{ color: 'var(--rd-text-faint)' }} />
        </OriginLink>
      ) : (
        <p className="flex items-center gap-3 px-6 py-2.5 text-[13px]">{lead}<span className="min-w-0 flex-1 truncate">{body}</span></p>
      )}
    </li>
  )

  return (
    <RdModal
      title={`Sync · ${formatDate(run.finished_at ?? run.started_at)} · ${changes.length} ${changes.length === 1 ? 'change' : 'changes'}`}
      subtitle={`${orgName || 'Organisation'} · ${TRIGGER_LABEL[run.trigger]}`}
      width={800}
      onClose={onClose}
      testId="sync-summary-modal"
      bodyClassName="p-0"
      footerClassName="justify-between"
      footer={
        <>
          <p className="text-[12px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="sync-archived-note">
            {run.evidence_skipped_archived ? `${run.evidence_skipped_archived} archived evidence ${run.evidence_skipped_archived === 1 ? 'item' : 'items'} still ${run.evidence_skipped_archived === 1 ? 'matches' : 'match'} Discovery and ${run.evidence_skipped_archived === 1 ? 'was' : 'were'} not re-added.` : ''}
          </p>
          <button type="button" className="rd-btn" onClick={onClose}>Close</button>
        </>
      }
    >
      {grouped.length === 0 && <p className="px-6 py-8 text-center text-[13px]" style={{ color: 'var(--rd-text-muted)' }}>This sync changed nothing.</p>}
      {grouped.map((g) => (
        <section key={g.key} aria-label={g.label}>
          <p className="px-6 py-2 text-[12px] font-semibold" style={{ background: 'var(--rd-bg-header)', color: 'var(--rd-text-muted)' }}>{g.label} · {g.rows.length}</p>
          <ul>
            {g.rows.map((c, i) => {
              if (c.group === 'levels') {
                const f = findObjective(index, c.objective_code)
                const color = f ? domainColor(f.domain, theme) : undefined
                return objectiveRow(
                  c.objective_code,
                  c.kind === 'flag' ? <>confirmed <span className="rd-mono rd-id">{c.confirmed_level}</span>, system now suggests <span className="rd-mono rd-id">{c.to_level}</span></> : c.kind === 'coverage' ? <>suggests <span className="rd-mono rd-id">{c.to_level}</span> <CoverageChip inline /></> : <>suggests <span className="rd-mono rd-id" style={{ color }}>{c.to_level ?? '—'}</span> · awaiting confirmation</>,
                  `${g.key}-${i}`
                )
              }
              if (c.group === 'evidence') return objectiveRow(c.objective_code, <>{c.count} {c.source === 'discovery' ? (c.count === 1 ? 'inventory item' : 'inventory items') : c.source === 'playbook' ? (c.count === 1 ? 'Playbook item' : 'Playbook items') : c.count === 1 ? 'item' : 'items'}</>, `${g.key}-${i}`)
              if (c.group === 'coverage') return plainRow(`/readiness/progress${q}`, <><span style={{ color: 'var(--rd-coverage)' }}>+{(c.to - c.from).toLocaleString()} assets found</span> ({c.from.toLocaleString()} → {c.to.toLocaleString()})</>, `${g.key}-${i}`, <Chip tone="tile" style={{ background: 'var(--rd-coverage-bg)', color: 'var(--rd-coverage)' }}>coverage</Chip>)
              if (c.group === 'dependencies') return plainRow(c.technology_id ? `/readiness/catalogue/${c.technology_id}${q}` : `/readiness/catalogue${q}`, c.text, `${g.key}-${i}`, <Chip tone={c.kind === 'vendor_date_moved' ? 'warn' : 'tile'}>{c.kind === 'vendor_date_moved' ? 'vendor date' : c.kind === 'technology_detected' ? 'detected' : 'unmatched'}</Chip>)
              return plainRow(`/readiness/tracker${q}&item=${c.item_id}`, <><span className="rd-mono rd-id">{c.code}</span> · re-scan {c.outcome === 'cleared' ? 'cleared' : 'still vulnerable'}</>, `${g.key}-${i}`, <Chip tone={c.outcome === 'cleared' ? 'ok' : 'warn'}>{c.outcome === 'cleared' ? 'cleared' : 'still vulnerable'}</Chip>)
            })}
          </ul>
        </section>
      ))}
    </RdModal>
  )
}

// ── Sync summary from the readiness service ─────────────────────────────────
// What one sync changed on the open assessment: the levels it newly suggests
// (or withdraws), read from the service's objective history since the sync
// started, so a second sync with nothing new says so. Levels themselves never
// change on a sync; each row opens the objective to review it.

export interface ServedSyncSummary {
  startedAt: string
  entries: ActivityEntry[]
  failed: string[] // modules whose pull failed
}

export function ServedSyncSummaryModal({ ctx, summary, onOpen, onClose }: { ctx: ModuleCtx; summary: ServedSyncSummary; onOpen: (code: string) => void; onClose: () => void }) {
  const { theme } = useTheme()
  const { index, orgName } = ctx
  const rows = summary.entries.filter((e) => e.kind === 'suggested')
  return (
    <RdModal
      title={`Sync · ${formatDate(summary.startedAt)} · ${rows.length ? `${rows.length} ${rows.length === 1 ? 'change' : 'changes'}` : 'no changes'} since the last sync`}
      subtitle={`${orgName || 'Organisation'} · your levels are unchanged; new suggestions wait for your review`}
      width={760}
      onClose={onClose}
      testId="sync-summary-modal"
      bodyClassName="p-0"
      footerClassName="justify-end"
      footer={<button type="button" className="rd-btn" onClick={onClose}>Close</button>}
    >
      {summary.failed.length > 0 && (
        <p className="px-6 py-2 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' }}>{summary.failed.join(' and ')} could not be reached; its data is as of the previous sync.</p>
      )}
      {rows.length === 0 ? (
        <p className="px-6 py-8 text-center text-[13px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="sync-no-changes">Nothing changed since the last sync.</p>
      ) : (
        <ul>
          {rows.map((e) => {
            const f = findObjective(index, e.objective_code)
            const color = f ? domainColor(f.domain, theme) : undefined
            const to = levelCode(e.to_level)
            return (
              <li key={e.id} style={{ borderTop: '1px solid var(--rd-border-subtle)' }}>
                <button type="button" className="rd-qv-row flex w-full items-center gap-3 px-6 py-2.5 text-left text-[13px]" onClick={() => onOpen(e.objective_code)} data-testid={`sync-row-${e.objective_code}`}>
                  {f ? <DomainDot domain={f.domain} theme={theme} size={10} /> : <span className="inline-block h-2.5 w-2.5" />}
                  <span className="rd-mono rd-id font-semibold">{e.objective_code}</span>
                  <span className="min-w-0 flex-1">
                    {to ? <>{e.source === 'playbook' ? 'Playbook' : 'Discovery'} now suggests <span className="rd-mono rd-id" style={{ color }}>{to}</span>{e.from_level != null ? <> (was {levelCode(e.from_level)})</> : null}</> : <>suggestion withdrawn</>}
                    {e.note && <span className="block truncate text-[12px]" style={{ color: 'var(--rd-text-muted)' }} title={e.note}>{e.note}</span>}
                  </span>
                  <Chip tone="warn">review</Chip>
                  <Icon name="chevron-right" size={16} className="rd-qv-chevron shrink-0" style={{ color: 'var(--rd-text-faint)' }} />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </RdModal>
  )
}
